import React, { useEffect, useRef, useState, useCallback } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import DayPicker from 'react-day-picker';
import moment from 'moment';
import 'moment/locale/pl';
import 'moment/locale/en-gb';
import 'moment/locale/de';
import 'react-day-picker/lib/style.css';
import './style.scss';
import Button from '../Button'
import { t } from 'i18next'

import { getCurrentLanguage } from '../../helpers';
import {
    setStartDate,
    setEndDate,
    setLastMinute
} from '../../actions/formActions';
import classNames from 'classnames'

const currentLanguage = getCurrentLanguage();
moment.locale(currentLanguage);

const MONTHS = [...Array(12).keys()].map((i) => moment().month(i).format('MMMM'));
const WEEKDAYS_SHORT = [...Array(7).keys()].map((i) => moment().day(i).format('dd'));
const WEEKDAYS_LONG = [...Array(7).keys()].map((i) => moment().day(i).format('dddd'));
const DATE_FROM_TYPE = 'date_from';
const DATE_TO_TYPE = 'date_to';

const DateRangePicker = ({
     startDate: startDateProp,
     endDate: endDateProp,
     lastMinute,
     startLabel,
     endLabel,
     setStartDate,
     setEndDate,
     setLastMinute
 }) => {
    const calendarRef = useRef(null);
    const [isCalendarVisible, setIsCalendarVisible] = useState(false);
    const [currentSelectedDate, setCurrentSelectedDate] = useState(DATE_FROM_TYPE);
    const [firstDateCheck, setFirstDateCheck] = useState(true);
    const [height, setHeight] = useState(0)

    const start = startDateProp ? new Date(startDateProp) : undefined;
    const end = endDateProp ? new Date(endDateProp) : undefined;

    const handleClickOutside = useCallback((event) => {
        if (calendarRef.current && !calendarRef.current.contains(event.target)) {
            setIsCalendarVisible(false);
        }

        const tripOperatorsSlider = document.getElementById('trip-operators');
        if(tripOperatorsSlider) {
            tripOperatorsSlider.style.pointerEvents = 'auto';
        }
    }, []);

    useEffect(() => {
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [handleClickOutside]);

    // Set today's date as the default start date
    useEffect(() => {
        if (!startDateProp && !lastMinute) {
            const today = moment().format('YYYY-MM-DD');
            setStartDate(today);
        }
    }, [startDateProp, lastMinute, setStartDate]);

    useEffect(() => {
        if (lastMinute === true) {
            const today = moment().format('YYYY-MM-DD');
            setStartDate(today);
            setEndDate(null);
        }
    }, [lastMinute, setStartDate, setEndDate]);

    useEffect(() => {
        if (!isCalendarVisible) return
        if (!calendarRef.current) return

        const dayPickerEl = calendarRef.current.querySelector('.DayPicker')
        if (!dayPickerEl) {
            return
        }

        const observer = new ResizeObserver((entries) => {
            const { height } = entries[0].contentRect
            setHeight(height)
        })

        observer.observe(dayPickerEl)

        return () => {
            observer.disconnect()
        }
    }, [isCalendarVisible])

    const handleDayClick = (day, modifiers, e) => {
        const clickedDate = moment(day);
        const today = moment().startOf('day');
        const clickedFormatted = clickedDate.format('YYYY-MM-DD');

        // Check if the selected date is not earlier than today.
        if (clickedDate.isBefore(today)) {
            return;
        }

        // Check if the element is disabled.
        if (e && e.target) {
            const dayElement = e.target.closest('.DayPicker-Day');
            if (dayElement && (
                dayElement.classList.contains('DayPicker-Day--disabled') ||
                dayElement.getAttribute('aria-disabled') === 'true'
            )) {
                return;
            }
        }

        // Check the modifiers for additional certainty.
        if (modifiers && modifiers.disabled) {
            return;
        }

        if (lastMinute) {
            setLastMinute(false);
        }

        if (currentSelectedDate === DATE_FROM_TYPE) {
            if (end) {
                const endMoment = moment(end);
                if (clickedDate.isAfter(endMoment)) {
                    setStartDate(endMoment.format('YYYY-MM-DD'));
                    setEndDate(clickedFormatted);
                } else {
                    setStartDate(clickedFormatted);
                }
            } else {
                setStartDate(clickedFormatted);
            }
            setCurrentSelectedDate(DATE_TO_TYPE);
        } else if (currentSelectedDate === DATE_TO_TYPE) {
            const startMoment = moment(start);

            if (clickedDate.isAfter(startMoment)) {
                // The "to" date must be later than the "from" date.
                setEndDate(clickedFormatted);
                setCurrentSelectedDate(DATE_FROM_TYPE);
            } else {
                // If an earlier or equal date was clicked, set it as the new "from" date.
                setStartDate(clickedFormatted);
                setEndDate(null);
                setCurrentSelectedDate(DATE_TO_TYPE);
            }
        } else {
            setStartDate(clickedFormatted);
            setCurrentSelectedDate(DATE_TO_TYPE);
        }
    };

    const toggleCalendar = (dateType=null) => {
        if (firstDateCheck) {
            //setStartDate(undefined);
        }

        setFirstDateCheck(false);
        setCurrentSelectedDate(dateType)
        setIsCalendarVisible((prev) => !prev);

        const tripOperatorsSlider = document.getElementById('trip-operators');
        if(tripOperatorsSlider) {
            tripOperatorsSlider.style.pointerEvents = 'none';
        }
    };

    const formattedStartDate = start
        ? moment(start).format('DD.MM.YYYY')
        : moment().format('DD.MM.YYYY');

    const formattedEndDate = end
        ? moment(end).format('DD.MM.YYYY')
        : t('form.dates.end_field_placeholder');

    const modifiers = {
        start,
        end,
        range: start && end ? { from: start, to: end } : undefined
    };

    const selectedDays = start && end
        ? [start, { from: start, to: end }]
        : start
            ? [start]
            : [];

    // Disable past dates.
    const disabledDays = [{ before: new Date() }];

    const handleClear = () => {
        setStartDate(null);
        setEndDate(null);
        setCurrentSelectedDate(DATE_FROM_TYPE);
    };
    
    const handleSave = () => {
        setIsCalendarVisible(false);
    
        const tripOperatorsSlider = document.getElementById('trip-operators');
        if (tripOperatorsSlider) {
            tripOperatorsSlider.style.pointerEvents = 'auto';
        }
    };

    return (
        <>
            <div className="trip-field__calendar">
                <div className="trip-field__date" onClick={() => toggleCalendar(DATE_FROM_TYPE)}>
                    <span
                        className={classNames('trip-field__title', {
                            'trip-field--selected': isCalendarVisible && currentSelectedDate === DATE_FROM_TYPE,
                        })}
                    >
                        {startLabel}
                    </span>
                    <span className="trip-field__label">{formattedStartDate}</span>
                </div>
                <div className="trip-field__date" onClick={() => toggleCalendar(DATE_TO_TYPE)}>
                    <span
                        className={classNames('trip-field__title', {
                            'trip-field--selected': isCalendarVisible && currentSelectedDate === DATE_TO_TYPE,
                        })}
                    >
                        {endLabel}
                    </span>
                    <span className="trip-field__label">{formattedEndDate}</span>
                </div>
            </div>
            {isCalendarVisible && (
                <div ref={calendarRef}>
                    <DayPicker
                        firstDayOfWeek={1}
                        selectedDays={selectedDays}
                        onDayClick={handleDayClick}
                        disabledDays={disabledDays}
                        modifiers={modifiers}
                        numberOfMonths={window.innerWidth > 768 ? 2 : 1}
                        months={MONTHS}
                        weekdaysShort={WEEKDAYS_SHORT}
                        weekdaysLong={WEEKDAYS_LONG}
                    />
                    <div className="date-picker__footer" style={{
                            bottom: height > 0 ? `${-height}px` : undefined,
                        }}>
                        <Button
                            clickHandler={handleClear}
                            label={t('common.btn_reset')}
                            variant="secondary"
                            extraCss="trip-btn--reset"
                        />
                        <Button
                            clickHandler={handleSave}
                            label={t('common.btn_save')}
                            variant="primary"
                            extraCss="trip-btn--save"
                        />
                    </div>
                </div>
            )}
        </>
    )
};

const mapStateToProps = (state) => ({
    startDate: state.formReducer.od,
    endDate: state.formReducer.do,
    lastMinute: state.formReducer.lastMinute
});

const mapDispatchToProps = (dispatch) =>
    bindActionCreators(
        {
            setStartDate,
            setEndDate,
            setLastMinute
        },
        dispatch
    );

export default connect(mapStateToProps, mapDispatchToProps)(DateRangePicker);
